import {
  Box,
  Code,
  Divider,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList
} from '@chakra-ui/react'
import React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownMessageProps {
  content: string
}

/**
 * Convert LaTeX formulas to readable plain text.
 * Ported from jamiipesa-ai MessageFormatter approach.
 */
function convertLatexFormula(formula: string, preserveFormatting = false): string {
  let readable = preserveFormatting ? formula : formula.trim()

  // Drop display/environment noise
  readable = readable.replace(/\\begin\{[^}]+\}/g, '')
  readable = readable.replace(/\\end\{[^}]+\}/g, '')
  readable = readable.replace(/\\displaystyle\b/g, '')

  // Spacing commands
  readable = readable.replace(/\\qquad\b/g, ' ')
  readable = readable.replace(/\\quad\b/g, ' ')
  readable = readable.replace(/\\[,;:!]/g, ' ')
  readable = readable.replace(/\\ /g, ' ')

  // Exponents: ^{...} → ^(...)
  for (let i = 0; i < 10; i++) {
    const before = readable
    readable = readable.replace(
      /\^\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g,
      (_m, exp) => `^(${exp.trim()})`
    )
    readable = readable.replace(/\^([0-9]+|[a-zA-Z])\b/g, (_m, exp) => `^(${exp})`)
    if (readable === before) break
  }

  // Subscripts: _{...} → (...) or _name
  for (let i = 0; i < 10; i++) {
    const before = readable
    readable = readable.replace(/_\{([^{}]+)\}/g, (_m, sub) => {
      const s = sub.trim()
      return /^[a-zA-Z]{2,}$/.test(s) ? `_${s}` : `(${s})`
    })
    if (readable === before) break
  }
  readable = readable.replace(/_([0-9a-zA-Z]+)/g, (_m, sub) => {
    return /^[a-zA-Z]{2,}$/.test(sub) ? `_${sub}` : `(${sub})`
  })

  // Summation / product
  readable = readable.replace(/\\sum\b/g, 'Σ')
  readable = readable.replace(/\\prod\b/g, 'Π')
  readable = readable.replace(/\\infty\b/g, '∞')

  // Math functions
  const funcs = ['ln', 'log', 'exp', 'sin', 'cos', 'tan', 'max', 'min']
  for (const fn of funcs) {
    const pattern = new RegExp(`\\\\${fn}\\s*\\{([^{}]*(?:\\{[^{}]*\\}[^{}]*)*)\\}`, 'g')
    readable = readable.replace(pattern, (_m, arg) => `${fn}(${arg.trim()})`)
    readable = readable.replace(new RegExp(`\\\\${fn}\\b`, 'g'), fn)
  }

  // sqrt
  readable = readable.replace(
    /\\sqrt(?:\s*\{([^}]+)\}|\s*\[([^\]]+)\]\s*\{([^}]+)\})/g,
    (_m, simple, index, arg) => {
      if (index && arg) return `√[${index}](${arg.trim()})`
      return `√(${(simple || arg || '').trim()})`
    }
  )

  // Unwrap styling commands: \text{...}, \textbf{...}, \mathrm{...}, \boxed{...} etc.
  const unwrapCmds = [
    'text',
    'textbf',
    'textit',
    'textrm',
    'mathrm',
    'mathbf',
    'mathit',
    'operatorname',
    'boxed',
    'overline',
    'underline',
    'boldsymbol',
    'mbox'
  ]
  for (let pass = 0; pass < 6; pass++) {
    const before = readable
    for (const cmd of unwrapCmds) {
      const pattern = new RegExp(`\\\\${cmd}\\s*\\{([^{}]*(?:\\{[^{}]*\\}[^{}]*)*)\\}`, 'g')
      readable = readable.replace(pattern, (_m, inner) => inner.trim())
    }
    if (readable === before) break
  }

  // Greek letters
  const greek: Record<string, string> = {
    alpha: 'α',
    beta: 'β',
    gamma: 'γ',
    delta: 'δ',
    epsilon: 'ε',
    theta: 'θ',
    lambda: 'λ',
    mu: 'μ',
    pi: 'π',
    rho: 'ρ',
    sigma: 'σ',
    tau: 'τ',
    phi: 'φ',
    omega: 'ω',
    Delta: 'Δ',
    Sigma: 'Σ',
    Pi: 'Π',
    Omega: 'Ω'
  }
  for (const [name, symbol] of Object.entries(greek)) {
    readable = readable.replace(new RegExp(`\\\\${name}\\b`, 'g'), symbol)
  }

  // Fractions: \frac{a}{b} → a ÷ b
  for (let i = 0; i < 10; i++) {
    const before = readable
    readable = readable.replace(
      /\\(?:frac|dfrac|tfrac)\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g,
      (_m, num, den) => {
        let n = num.trim().replace(/\\text\{([^}]+)\}/g, '$1')
        let d = den.trim().replace(/\\text\{([^}]+)\}/g, '$1')
        n = n
          .replace(/\\([a-zA-Z]+)/g, '$1')
          .replace(/\\/g, '')
          .trim()
        d = d
          .replace(/\\([a-zA-Z]+)/g, '$1')
          .replace(/\\/g, '')
          .trim()
        return `${n} ÷ ${d}`
      }
    )
    if (readable === before) break
  }

  // \left / \right delimiters
  readable = readable.replace(/\\left\./g, '')
  readable = readable.replace(/\\right\./g, '')
  readable = readable.replace(/\\left\(/g, '(')
  readable = readable.replace(/\\right\)/g, ')')
  readable = readable.replace(/\\left\[/g, '[')
  readable = readable.replace(/\\right\]/g, ']')
  readable = readable.replace(/\\left\{/g, '{')
  readable = readable.replace(/\\right\}/g, '}')
  readable = readable.replace(/\\left\|/g, '|')
  readable = readable.replace(/\\right\|/g, '|')
  readable = readable.replace(/\\left\b/g, '')
  readable = readable.replace(/\\right\b/g, '')

  // Operators
  readable = readable.replace(/\\cdot/g, ' × ')
  readable = readable.replace(/\\times/g, ' × ')
  readable = readable.replace(/\\div/g, ' ÷ ')
  readable = readable.replace(/\\pm/g, ' ± ')
  readable = readable.replace(/\\approx/g, ' ≈ ')
  readable = readable.replace(/\\leq\b|\\le\b/g, ' ≤ ')
  readable = readable.replace(/\\geq\b|\\ge\b/g, ' ≥ ')
  readable = readable.replace(/\\neq\b|\\ne\b/g, ' ≠ ')
  readable = readable.replace(/\\rightarrow\b|\\to\b/g, ' → ')
  readable = readable.replace(/\\ldots\b|\\dots\b|\\cdots\b/g, '...')

  // Escaped characters
  readable = readable.replace(/\\%/g, '%')
  readable = readable.replace(/\\\$/g, '$')
  readable = readable.replace(/\\&/g, '&')
  readable = readable.replace(/\\_/g, '_')

  // Safety net: unwrap any remaining \command{...} → inner
  for (let i = 0; i < 8; i++) {
    const next = readable.replace(
      /\\[a-zA-Z]+\s*\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g,
      (_m, inner) => inner
    )
    if (next === readable) break
    readable = next
  }
  // Strip bare \commands
  readable = readable.replace(/\\[a-zA-Z]+\b/g, '')
  // Orphan braces
  readable = readable.replace(/\{([^{}]*)\}/g, '$1')

  // Ensure spacing around arithmetic operators that appear between values
  // +  (but not leading +  like "+5")
  readable = readable.replace(/(\w)\+(\w)/g, '$1 + $2')
  // -  (but not leading -  like "-5" or "e-10")
  readable = readable.replace(/(\d)\s*-\s*(\d)/g, (match, before, after) => {
    // Preserve subtraction, but check if it looks like a negative exponent (e.g. "e-10")
    const idx = readable.indexOf(match)
    if (idx > 0 && readable[idx - 1]?.toLowerCase() === 'e') return match
    return `${before} - ${after}`
  })
  // =
  readable = readable.replace(/(\w)\s*=\s*(\w)/g, '$1 = $2')

  // Clean spacing
  readable = readable.replace(/\s+/g, ' ').trim()
  return readable
}

/**
 * Pre-process content to convert LaTeX to readable text before markdown rendering.
 * Handles: \[...\], [...] (plain brackets), $$...$$, $...$, and inline LaTeX.
 */
function preprocessLatex(content: string): string {
  // Handle \[ ... \] display math
  content = content.replace(/\\\[([\s\S]*?)\\\]/g, (_m, formula) => convertLatexFormula(formula))

  // Handle \( ... \) inline math
  content = content.replace(/\\\(([\s\S]*?)\\\)/g, (_m, formula) => convertLatexFormula(formula))

  // Handle $$ ... $$ display math
  content = content.replace(/\$\$([\s\S]*?)\$\$/g, (_m, formula) => convertLatexFormula(formula))

  // Handle plain [ ... ] lines with LaTeX commands (model quirk)
  const lines = content.split('\n')
  const result: string[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (
      trimmed.length > 2 &&
      trimmed.startsWith('[') &&
      trimmed.endsWith(']') &&
      /\\[a-zA-Z]/.test(trimmed)
    ) {
      const inner = trimmed.slice(1, -1).trim()
      result.push(convertLatexFormula(inner))
    } else if (
      /\\[a-zA-Z]+/.test(trimmed) &&
      /\\(?:text|frac|times|approx|div|sqrt|left|right)/.test(trimmed)
    ) {
      // Lines with naked LaTeX commands (no delimiters)
      result.push(convertLatexFormula(trimmed, true))
    } else {
      result.push(line)
    }
  }

  content = result.join('\n')

  // Handle inline $...$ (careful not to match currency)
  content = content.replace(/\$([^$\n]+?)\$/g, (match, formula) => {
    const hasLatex = /\\[a-zA-Z]|[_^]\{/.test(formula)
    if (hasLatex) return convertLatexFormula(formula)
    return match
  })

  return content
}

const HEADING_SIZE: Record<number, string> = {
  1: 'md',
  2: 'sm',
  3: 'xs',
  4: 'xs',
  5: 'xs',
  6: 'xs'
}

const components: Components = {
  h1: ({ children }) => (
    <Heading as="h1" size={HEADING_SIZE[1]} fontWeight="bold" mt={3} mb={2} color="gray.800">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" size={HEADING_SIZE[2]} fontWeight="bold" mt={3} mb={2} color="gray.800">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" size={HEADING_SIZE[3]} fontWeight="bold" mt={3} mb={2} color="gray.800">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading as="h4" size={HEADING_SIZE[4]} fontWeight="bold" mt={2} mb={1} color="gray.800">
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading as="h5" size={HEADING_SIZE[5]} fontWeight="bold" mt={2} mb={1} color="gray.800">
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading as="h6" size={HEADING_SIZE[6]} fontWeight="bold" mt={2} mb={1} color="gray.800">
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text mb={2} lineHeight="1.6">
      {children}
    </Text>
  ),
  a: ({ href, children }) => (
    <Link href={href} color="brand.500" isExternal textDecoration="underline">
      {children}
    </Link>
  ),
  strong: ({ children }) => (
    <Text as="span" fontWeight="bold">
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text as="span" fontStyle="italic">
      {children}
    </Text>
  ),
  del: ({ children }) => (
    <Text as="del" opacity={0.7}>
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <UnorderedList pl={4} mb={2} spacing={1}>
      {children}
    </UnorderedList>
  ),
  ol: ({ children }) => (
    <OrderedList pl={4} mb={2} spacing={1}>
      {children}
    </OrderedList>
  ),
  li: ({ children }) => <ListItem>{children}</ListItem>,
  blockquote: ({ children }) => (
    <Box
      borderLeft="3px solid"
      borderColor="brand.300"
      pl={3}
      py={1}
      mb={2}
      color="gray.600"
      fontStyle="italic"
    >
      {children}
    </Box>
  ),
  hr: () => <Divider my={3} />,
  code: ({ inline, children }) => {
    if (inline) {
      return (
        <Code fontSize="0.85em" px={1} borderRadius="sm" bg="blackAlpha.100">
          {children}
        </Code>
      )
    }
    return (
      <Box
        as="pre"
        bg="gray.800"
        color="gray.100"
        p={3}
        borderRadius="md"
        mb={2}
        overflowX="auto"
        fontSize="0.8em"
        whiteSpace="pre"
      >
        <Code bg="transparent" color="inherit" p={0} whiteSpace="pre">
          {children}
        </Code>
      </Box>
    )
  },
  table: ({ children }) => (
    <TableContainer mb={2}>
      <Table size="sm" variant="simple">
        {children}
      </Table>
    </TableContainer>
  ),
  thead: ({ children }) => <Thead>{children}</Thead>,
  tbody: ({ children }) => <Tbody>{children}</Tbody>,
  tr: ({ children }) => <Tr>{children}</Tr>,
  th: ({ children }) => (
    <Th fontSize="xs" textTransform="none">
      {children}
    </Th>
  ),
  td: ({ children }) => <Td fontSize="xs">{children}</Td>
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content }) => {
  return (
    <Box
      fontSize="sm"
      lineHeight="1.6"
      sx={{
        '& > *:first-of-type': { marginTop: 0 },
        '& > *:last-child': { marginBottom: 0 }
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {preprocessLatex(content)}
      </ReactMarkdown>
    </Box>
  )
}

export default MarkdownMessage
